import type { Readable } from "stream";
import { z } from "zod";

import type { FileListOrder, FilePurpose } from "./batch.models";

/** ******************************************************************************
 *  Types
 ******************************************************************************* */

export const MulterFile = z.object({
  fieldname: z.string(),
  originalname: z.string(),
  encoding: z.string(),
  mimetype: z.string(),
  buffer: z.instanceof(Buffer<ArrayBufferLike>),
  size: z.number(),
});

export type MulterFile = z.infer<typeof MulterFile>;

export const UploadedFileResponseSchema = z.object({
  fileKey: z.string(),
  createdAt: z.date(),
  updatedAt: z.date(),
});

export type UploadedFileResponse = z.infer<typeof UploadedFileResponseSchema>;

export const FilesTableRecordSchema = UploadedFileResponseSchema.extend({
  _id: z.string(),
  teamId: z.string(),
  projectId: z.string().nullable(),
  lineCount: z.number(),
});

export type FilesTableRecord = z.infer<typeof FilesTableRecordSchema>;

export type StreamUploadOptions = {
  originalname: string;
  teamId: string;
  projectId: string | null;
  mimetype: string;
  filenamePrefix?: string;
};

export type MultipartFileUploadPart = {
  partNumber: number;
  etag: string;
};

export type MultipartFileUploadInitiateOptions = {
  originalname: string;
  teamId: string;
  mimetype: string;
  filenamePrefix?: string;
  fileSize: number;
  partSizeBytes?: number;
};

export type MultipartFileUploadInitiateResponse = {
  r2UploadId: string;
  r2Key: string;
  partSizeBytes: number;
  totalParts: number;
};

export type MultipartFileUploadPartOptions = {
  teamId: string;
  r2UploadId: string;
  r2Key: string;
  partNumber: number;
  body: Blob | ReadableStream<Uint8Array> | ArrayBuffer;
};

export type CompleteMultipartFileUploadOptions = {
  teamId: string;
  r2UploadId: string;
  r2Key: string;
  parts: MultipartFileUploadPart[];
};

export type AbortMultipartFileUploadOptions = {
  teamId: string;
  r2UploadId: string;
  r2Key: string;
};

export type CreateUploadedFileRecordOptions = {
  teamId: string;
  projectId: string;
  r2Key: string;
  lineCount: number;
};

/** ******************************************************************************
 *  Storage Interface
 ******************************************************************************* */

export type FileStorageInterface = {
  byId: (fileId: string) => Promise<FilesTableRecord | null>;
  uploadFile: (file: MulterFile, key: string) => Promise<UploadedFileResponse>;

  uploadStream: (
    stream: Readable,
    options: StreamUploadOptions,
  ) => Promise<FilesTableRecord>;

  initiateMultipartUpload: (
    options: MultipartFileUploadInitiateOptions,
  ) => Promise<MultipartFileUploadInitiateResponse>;

  uploadMultipartPart: (
    options: MultipartFileUploadPartOptions,
  ) => Promise<MultipartFileUploadPart>;

  completeMultipartUpload: (
    options: CompleteMultipartFileUploadOptions,
  ) => Promise<void>;

  abortMultipartUpload: (
    options: AbortMultipartFileUploadOptions,
  ) => Promise<void>;

  getObjectStreamByKey: (options: {
    teamId: string;
    r2Key: string;
  }) => Promise<ReadableStream<Uint8Array> | null>;

  deleteObjectByKey: (options: {
    teamId: string;
    r2Key: string;
  }) => Promise<void>;

  createUploadedFileRecord: (
    options: CreateUploadedFileRecordOptions,
  ) => Promise<FilesTableRecord>;

  uploadContent: (
    content: string,
    options: {
      teamId: string;
      projectId: string;
      filename: string;
      mimetype?: string;
    },
  ) => Promise<FilesTableRecord>;

  retrieveFileUrl: (fileId: string) => Promise<string | null>;
  deleteFile: (fileId: string) => Promise<void>;

  /**
   * Team files under this store's prefix in `order` of creation, paged after
   * `after` (a file id), optionally only those with `purpose`. A non-null
   * `projectId` keeps only that project's files and legacy files with no project.
   */
  listTeamFiles: (options: {
    teamId: string;
    projectId: string | null;
    limit: number;
    after?: string;
    purpose?: FilePurpose;
    order: FileListOrder;
  }) => Promise<{ files: FilesTableRecord[]; hasMore: boolean }>;

  /** Stored size in bytes of a team object, or null when it is missing. */
  getObjectSizeByKey: (options: {
    teamId: string;
    r2Key: string;
  }) => Promise<number | null>;
};
